import * as Yup from 'yup';
import React from 'react';
import {
	Deal,
	DealReducedFeeLender,
	Guid,
	PartialBank,
	Tier1BankTypeMap,
	Tier1BankTypes,
	getReducedFeeLendersBase,
} from '../../shared/models';
import {
	CerebroCheckbox,
	CerebroDrawer,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroForm,
	CerebroHeading,
	CerebroInput,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroText,
} from '../../shared/components';
import { Field, Formik, FormikErrors } from 'formik';
import { Grid } from '@mui/material';
import { FormikUtils } from '../../shared/utils';
import { LendersAutoComplete } from './LendersAutoComplete';
import { useMutation, useQueryClient } from 'react-query';
import { useApi } from '../../shared/context';
import { useSnackbar } from 'notistack';
import { QueryKeys } from '../../shared/hooks';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { AxiosResponse } from 'axios';
import { getApiResponseData } from '../../shared/utils/axios';

interface ReducedFeeLenderDrawerProps {
	open: boolean;
	onClose: () => void;
	deal: Deal | undefined;
	lenders: DealReducedFeeLender[] | undefined;
}

export const ReducedFeeLenderDrawer: React.FC<ReducedFeeLenderDrawerProps> = ({ open, onClose, deal, lenders }) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const ref = React.useRef<HTMLDivElement>(null);
	const [leaveDrawerOpen, setLeaveDrawerOpen] = React.useState<boolean>(false);

	const lenderMutation = useMutation(
		async ({ values }: ReducedFeeLenderMutationVariables) => {
			const createDto: CreateDealReducedFeeLenderDto = {
				tier1BankType: values.tier1BankType ?? null,
			};

			if (values.newLender) createDto.bankName = values.newLenderName;
			else createDto.bankGuid = values.lender?.guid;

			let updatedDeal: Deal | null = null;

			// If they are excluding a lender but haven't saved the toggle for excluding lenders, save the toggle to true.
			if (deal?.hasReducedFeeLenders !== true) {
				const { data, error } = await getApiResponseData<Deal>(
					api.patch(CerebroApiEndpoints.Deals.Root(deal?.guid), {
						hasReducedFeeLenders: true,
					})
				);

				if (error) {
					throw new Error(error.response?.data);
				}

				updatedDeal = data;
			}

			const { data, error } = await getApiResponseData<DealReducedFeeLender>(
				api.post<CreateDealReducedFeeLenderDto, AxiosResponse<DealReducedFeeLender>>(
					CerebroApiEndpoints.ReducedFeeLenders.Root(deal?.guid),
					createDto
				)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return { updatedDeal, data };
		},
		{
			onSuccess: ({ updatedDeal, data }) => {
				if (updatedDeal !== null) client.setQueryData(QueryKeys.Deal.Root(deal?.guid), updatedDeal);

				client.invalidateQueries(QueryKeys.ReducedFeeLenders(deal?.guid));

				snackbar.enqueueSnackbar(`${data?.bankName} has now been included as a reduced fee lender.`, {
					variant: 'success',
				});
			},
			onError: (error: Error) => {
				client.invalidateQueries(QueryKeys.ReducedFeeLenders(deal?.guid));
				snackbar.enqueueSnackbar(error.message, { variant: 'error' });
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
				if (leaveDrawerOpen) {
					helpers.resetForm({
						values: {
							lender: null,
							newLender: false,
							newLenderName: null,
						} as AddReducedFeeLenderFormValues,
					});
				} else {
					onClose();
				}
			},
		}
	);

	return (
		<CerebroDrawer ref={ref} open={open} onClose={onClose}>
			<CerebroHeading sx={{ marginBottom: 3 }}>Reduced Fee Lender</CerebroHeading>
			<Formik
				// validationSchema={AddReducedFeeLenderSchema}
				validate={validateForm}
				onSubmit={FormikUtils.MutateOnSubmit(lenderMutation.mutate)}
				initialValues={
					{
						lender: null,
						newLender: false,
						newLenderName: null,
						tier1BankType: null,
					} as AddReducedFeeLenderFormValues
				}
			>
				{({ isSubmitting, values, touched, errors, handleSubmit }) => (
					<CerebroForm flatIndicator onSubmit={handleSubmit} sx={{ paddingRight: 3, minWidth: { md: 500 } }}>
						<CerebroLabel htmlFor="lender" sx={{ marginTop: 3 }}>
							Select a reduced fee lender:
						</CerebroLabel>
						<Field
							id="lender"
							name="lender"
							lenders={getReducedFeeLendersBase(lenders)}
							component={LendersAutoComplete}
							companyGuid={deal?.companyGuid}
							disabled={values.newLender}
							error={FormikUtils.IsInvalid('lender', errors, touched)}
						/>

						<Grid container justifyContent="space-between" alignItems="center" spacing={2}>
							<Grid item>
								{FormikUtils.IsInvalid('lender', errors, touched) && (
									<CerebroText variant="error">{errors.lender}</CerebroText>
								)}
							</Grid>
							<Grid item>
								<CerebroLabel htmlFor="newLender" sx={{ display: 'flex', alignItems: 'center' }}>
									<Field
										id="newLender"
										name="newLender"
										size="small"
										as={CerebroCheckbox}
										sx={{ padding: 0, paddingRight: 1 }}
									/>
									<CerebroText variant="hint" component="span">
										Include a lender not listed
									</CerebroText>
								</CerebroLabel>
							</Grid>
						</Grid>

						{values.newLender && (
							<>
								<CerebroLabel htmlFor="newLenderName" sx={{ marginTop: 3 }}>
									New Lender:
								</CerebroLabel>
								<Field
									id="newLenderName"
									name="newLenderName"
									as={CerebroInput}
									placeholder="The full name of the lender to exclude"
									error={FormikUtils.IsInvalid('newLenderName', errors, touched)}
								/>
								{FormikUtils.IsInvalid('newLenderName', errors, touched) && (
									<CerebroText variant="error">{errors.newLenderName}</CerebroText>
								)}

								<CerebroLabel id="tier1BankType" sx={{ marginTop: 3 }}>
									Lender Type
								</CerebroLabel>
								<Field
									id="tier1BankType"
									as={CerebroDropdown}
									name="tier1BankType"
									disabled={isSubmitting}
									error={FormikUtils.IsInvalid('tier1BankType', errors, touched)}
								>
									{Object.entries(Tier1BankTypeMap).map(([value, description]) => (
										<CerebroDropdownMenuItem key={value} value={value}>
											{description}
										</CerebroDropdownMenuItem>
									))}
								</Field>
								{FormikUtils.IsInvalid('tier1BankType', errors, touched) && (
									<CerebroText variant="error">{errors.tier1BankType}</CerebroText>
								)}
							</>
						)}

						<Grid container spacing={3}>
							<Grid item xs={6}>
								<CerebroPrimaryButton
									loading={isSubmitting}
									type="submit"
									onClick={() => setLeaveDrawerOpen(false)}
									sx={{ marginTop: 4 }}
								>
									Save &#38; Continue
								</CerebroPrimaryButton>
							</Grid>

							<Grid item xs={6}>
								<CerebroPrimaryButton
									loading={isSubmitting}
									type="submit"
									onClick={() => setLeaveDrawerOpen(true)}
									sx={{ marginTop: 4 }}
								>
									Save &#38; Add Another
								</CerebroPrimaryButton>
							</Grid>
						</Grid>
					</CerebroForm>
				)}
			</Formik>
		</CerebroDrawer>
	);
};

const AddReducedFeeLenderSchema = Yup.object().shape({
	newLender: Yup.boolean().required(),

	lender: Yup.object()
		.nullable(true)
		.when('newLender', {
			is: true,
			then: schema => schema.notRequired(),
			otherwise: schema => schema.required('Please select a lender to exclude'),
		}),
	tier1BankType: Yup.mixed<Tier1BankTypes>()
		.nullable(true)
		.when('newLender', {
			is: true,
			then: schema => schema.required('Please select a Tier 1 Bank Type'),
			otherwise: schema => schema.notRequired(),
		}),

	newLenderName: Yup.string()
		.nullable(true)
		.when('newLender', {
			is: true,
			then: schema => schema.required('Please provide the name of the lender to exclude'),
			otherwise: schema => schema.notRequired(),
		}),
});

function validateForm(values: AddReducedFeeLenderFormValues) {
	const errors: AddReducedFeeLenderFormValueErrors = {};

	if (values.newLender) {
		if (!values.newLenderName) {
			errors.newLenderName = 'You must enter a valid bank name.';
		}

		if (!values.tier1BankType) {
			errors.tier1BankType = 'You must select a type to add a lender.';
		}
	} else {
		if (!values.lender || !values.lender.name) {
			errors.lender = 'You must select a lender associated to the terms.';
		}
	}

	return errors;
}

interface AddReducedFeeLenderFormValues {
	lender: PartialBank | null;
	newLender: boolean;
	newLenderName: string | null;
	tier1BankType: Tier1BankTypes | null;
}
type AddReducedFeeLenderFormValueErrors = FormikErrors<AddReducedFeeLenderFormValues>;
type ReducedFeeLenderMutationVariables = FormikUtils.MutateOnSubmitVariables<AddReducedFeeLenderFormValues>;

interface CreateDealReducedFeeLenderDto {
	bankGuid?: Guid | null;
	bankName?: string | null;
	tier1BankType: Tier1BankTypes | null;
}
